import { loadModules } from "esri-loader";
import moment from "moment-timezone";
import { flatten } from "ramda";

export const queryLayers = async ({
  ids,
  view,
  imagery,
  outFields,
  countOnly = false,
}) => {
  const [Query] = await loadModules(["esri/rest/support/Query"]);
  const datasetIds =
    ids && ids.length > 0 ? ` AND DatasetID IN (${ids.join(",")})` : "";
  let query = new Query();
  query.outFields = outFields ? outFields : ["DatasetID"];
  const where = `NAME NOT LIKE 'Ov_%'${datasetIds}`;
  query.where = where;
  query.geometry = view.extent;
  query.returnGeometry = true;
  query.spatialRelationship = "envelope-intersects";
  let queryTask = imagery.queryTask;
  if (countOnly) {
    return queryTask.executeForCount(query);
  }
  return queryTask.execute(query);
};

export const removeLayers = ({ view, type = "graphics" }) => {
  view.map.layers.removeMany(view.map.layers.filter((l) => l.type === type));
};

export const setFootprintGraphics = async ({
  view,
  imagery,
  ids,
  outFields,
  appName,
}) => {
  const [GraphicsLayer] = await loadModules(["esri/layers/GraphicsLayer"]);
  const { features } = await queryLayers({
    ids,
    view,
    imagery,
    outFields,
  });
  const graphics = flatten(
    await Promise.all([
      createGraphics({ features, createSymbol: createFootprintSymbol }),
      createGraphics({ features, createSymbol: createLabelSymbol, appName }),
    ])
  );
  const layer = new GraphicsLayer({
    graphics,
  });
  removeLayers({ view });
  view.map.layers.add(layer);
};

const createGraphics = async ({ features, createSymbol, appName }) => {
  const [Graphic] = await loadModules(["esri/Graphic"]);
  return features.map((feature) => {
    const geometry = createPolygon(feature);
    const symbol = createSymbol(feature, appName);
    return new Graphic({
      symbol,
      attributes: feature.attributes,
      geometry,
    });
  });
};

const createPolygon = (feature) => ({
  rings: feature.geometry.rings,
  spatialReference: feature.geometry.spatialReference,
  type: "polygon",
});

const createFootprintSymbol = (feature) => ({
  type: "simple-fill",
  outline: {
    width: 1.5,
    color: [241, 179, 0, 1],
  },
  color: [241, 179, 0, 0.25],
});

const createLabelSymbol = (feature, appName) => {
  let title;
  if (appName === "topographic") {
    title = moment
      .tz(feature.attributes.DateYear, "US/Pacific")
      .utcOffset("+0400")
      .format("YYYY");
  } else {
    const { AcqDateFirst, AcqDateLast } = feature.attributes;
    const first = moment
      .tz(AcqDateFirst, "US/Pacific")
      .utcOffset("+0400")
      .format("YYYYMMDD");
    const last = moment
      .tz(AcqDateLast, "US/Pacific")
      .utcOffset("+0400")
      .format("YYYYMMDD");
    title = first === last ? `${first}` : `${first}-${last}`;
  }

  return {
    type: "text",
    text: `${title}`,
    color: "white",
    haloColor: [0, 0, 0, 0.5],
    haloSize: "1px",
    font: {
      size: 12,
      weight: "bold",
    },
    rotated: false,
  };
};
